export interface IVimeoSource {
  id: string
  // Privacy hash for unlisted videos. Vimeo requires it in the embed URL as
  // `?h=<hash>`; without it the player returns 403 ("Privacy"). Public videos
  // have no hash.
  hash?: string
}

// Parses a Vimeo id and (for unlisted videos) its privacy hash out of the value
// stored in `media.fileUrl`. Handles every shape we persist or receive:
//   https://vimeo.com/123                     -> { id: '123' }              (public)
//   https://vimeo.com/123/abc123?fl=pl        -> { id: '123', hash: 'abc123' } (unlisted, path form)
//   https://vimeo.com/123?h=abc123            -> { id: '123', hash: 'abc123' } (unlisted, query form)
//   https://player.vimeo.com/video/123?h=abc  -> { id: '123', hash: 'abc' }
// Returns null when no numeric Vimeo id is present.
export function parseVimeoSource(url?: string | null): IVimeoSource | null {
  if (!url) return null

  // The path-form hash is matched as hex (Vimeo hashes are hex) so unrelated
  // trailing segments (e.g. `vimeo.com/123/settings`) aren't taken for a hash.
  const path = url.match(
    /vimeo\.com\/(?:video\/)?(\d+)(?:\/([0-9a-fA-F]{6,}))?/,
  )
  if (!path) return null

  const queryHash = url.match(/[?&]h=([A-Za-z0-9]+)/)
  const hash = path[2] || queryHash?.[1]

  return hash ? { id: path[1], hash } : { id: path[1] }
}

// Defaults voltados a toque, alinhados ao que o player nativo já manda em
// `VimeoVideoPlayer/template.ts`:
//
// - `play_button_position`: no default (`auto`) o botão de play só vai para o
//   centro em breakpoints pequenos. Num player de tablet (~624px) ele fica na
//   barra de controles, um alvo de ~30px — abaixo dos 44px recomendados para
//   toque. E tocar no meio do vídeo não dá play no Vimeo, só alterna os
//   controles, então o aluno sente o player "sem sensibilidade". `center`
//   entrega o mesmo alvo grande do player do SparkVideos (vjs-big-play-centered).
// - `title`/`byline`/`portrait`: são links para o vimeo.com sobrepostos ao topo
//   do vídeo. Ocupam área clicável dentro do player e levam o aluno para fora.
// - `playsinline`: já é o default do Vimeo, explícito para não depender disso.
const TOUCH_DEFAULTS: Record<string, string | number> = {
  play_button_position: 'center',
  title: 0,
  byline: 0,
  portrait: 0,
  playsinline: 1,
}

// Builds the Vimeo player embed URL. The privacy hash comes first because Vimeo
// requires any extra params to follow it (`?h=<hash>&autoplay=0...`).
export function buildVimeoPlayerUrl(
  source: IVimeoSource,
  params: Record<string, string | number> = {},
): string {
  const query = new URLSearchParams()
  if (source.hash) query.set('h', source.hash)
  params = { ...TOUCH_DEFAULTS, ...params }
  Object.entries(params).forEach(([key, value]) =>
    query.set(key, String(value)),
  )

  const qs = query.toString()
  return `https://player.vimeo.com/video/${source.id}${qs ? `?${qs}` : ''}`
}
